/**
 * ============================================================================
 * MODE 1 — DIRECTIONAL EXPLORATION
 * ============================================================================
 * This page operates in MODE 1: Directional Exploration
 * 
 * PURPOSE: Orientation and clarity only
 * 
 * ALLOWED content:
 *   - Visual inspiration
 *   - Category names  
 *   - Directional language (tone, mood, positioning)
 * 
 * PROHIBITED content:
 *   - Brand names
 *   - Pricing
 *   - Domains
 *   - Ownership language
 *   - Purchase or checkout CTAs
 * 
 * APPROVED CTAs:
 *   - "Explore Direction"
 *   - "View Direction"
 *   - "Browse Directional Examples"
 *   - "Begin with Guided Creation"
 *   - "Browse Ready-Made Foundations"
 * 
 * DRIFT TEST (check before every deploy):
 *   1. Can someone buy from this page? → NO
 *   2. Can someone see a price from this page? → NO  
 *   3. Can someone think a brand is already chosen from this page? → NO
 * 
 * If any answer is YES → rollback immediately.
 * 
 * DEVELOPER NOTE:
 * Directional pages are non-commercial by design.
 * Conversion is intentionally delayed.
 * ============================================================================
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import CartDropdown from "../components/CartDropdown";
import { Button } from "../components/ui/button";
import { Leaf, Sparkles, Flame, Heart, Sun, ArrowRight, Package, Flower2, Home } from "lucide-react";
import { MODES } from "../config/ModeLock";

// Current page mode - DO NOT CHANGE
const PAGE_MODE = MODES.DIRECTIONAL_EXPLORATION;

const LandingPage = () => {
  const navigate = useNavigate();

  // ============================================================================
  // CATEGORY DIRECTION DATA — No brand names, prices, or ownership data allowed
  // These are VISUAL STARTING POINTS only, not products for sale
  // ============================================================================

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Only set checkoutReturnUrl if there isn't one already (to preserve cart checkout redirects)
    if (!sessionStorage.getItem('checkoutReturnUrl')) {
      sessionStorage.setItem('checkoutReturnUrl', '/dashboard');
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ============================================================================
  // UNIFIED NICHE DESCRIPTIONS
  // These categories represent connected brand directions — designed to work
  // as a system, not standalone ideas.
  // ============================================================================
  const categories = [
    {
      id: "soft_skincare",
      name: "Soft Skincare",
      description: "Visual direction for gentle, plant-forward skincare brands rooted in care and credibility.",
      helper: "No names. No pricing. Just clear brand direction.",
      icon: Sparkles,
      image: "https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg?w=800"
    },
    {
      id: "candles_aromatherapy",
      name: "Candles & Aromatherapy",
      description: "Mood-led brand direction for scent, ritual, and atmosphere — designed to feel intentional and grounded.",
      helper: "Explore the feeling, not the final brand.",
      icon: Flame,
      image: "https://images.unsplash.com/photo-1603006905003-be475563bc59?w=800&q=80"
    },
    {
      id: "dry_leaf_tea",
      name: "Dry Leaf Tea",
      description: "Calm, earthy brand direction for tea and herbal blends shaped by trust and daily ritual.",
      helper: "Details are refined later.",
      icon: Leaf,
      image: "https://images.pexels.com/photos/1793035/pexels-photo-1793035.jpeg?w=800"
    },
    {
      id: "natural_cosmetics",
      name: "Natural Cosmetics",
      description: "Soft, considered brand direction for beauty products rooted in simplicity and trust.",
      helper: "Direction first. Decisions later.",
      icon: Heart,
      image: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=800&q=80"
    },
    {
      id: "wellness_brands",
      name: "Wellness Brands",
      description: "Grounded brand direction for self-care, movement, and mindful living experiences.",
      helper: "Built for longevity, not trends.",
      icon: Sun,
      image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80"
    },
    {
      id: "home_rituals_living",
      name: "Home Rituals & Living",
      description: "Intentional brand direction for home, space, and everyday rituals that shape how a brand is lived with.",
      helper: "Designed to complete the system.",
      icon: Flower2,
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8] grain-texture">
      {/* Navigation */}
      <nav className="nav-glass fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center">
              <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Bloomwright Studio
              </span>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/store")}
              className="text-[#78716C] hover:text-[#2C332E] font-medium px-2 md:px-4"
              data-testid="nav-store-btn"
            >
              Store
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {/* Cart Dropdown */}
            <CartDropdown />
            <Button 
              onClick={handleLogin}
              className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6 py-2 hover-lift"
              data-testid="nav-login-btn"
            >
              Sign In with Google
            </Button>
          </div>
        </div>
      </nav>

      {/* ========================================= */}
      {/* SECTION 1 — HERO ANCHOR (Trust as Core) */}
      {/* ========================================= */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="space-y-6 animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Build a Brand People Trust
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#78716C] max-w-2xl mx-auto">
              AI-powered brand foundations for women building thoughtful, values-led product brands.
            </p>
            <p className="text-base text-[#78716C]/90 max-w-xl mx-auto">
              Bloomwright Studio removes the pressure from early brand decisions — so trust is built before visibility, not after.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================== */}
      {/* SECTION 2 — CONTEXT SETTER */}
      {/* ============================================== */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <p className="text-lg text-[#2C332E] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
            Trust doesn&apos;t come from rushing to launch.<br />
            It comes from clarity — in how a brand looks, sounds, and moves.
          </p>
          <p className="text-[#78716C]">
            Bloomwright helps you begin with direction, whether you&apos;re shaping something new or stepping into something already formed.
          </p>
        </div>
      </section>

      {/* ===================================================== */}
      {/* SECTION 3 — CATEGORY PREVIEW (Brand Directions)       */}
      {/* ===================================================== */}
      <section className="py-20 px-6 bg-[#F5F0EB]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl tracking-tight text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Explore Brand Directions by Category
            </h2>
            <p className="text-base text-[#78716C] max-w-2xl mx-auto">
              These categories represent connected brand directions — designed to work as a system, not standalone ideas.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <div 
                  key={category.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-hover transition-all duration-300 border-2 border-transparent hover:border-[#8DA399]/30"
                  data-testid={`category-preview-${category.id}`}
                >
                  {/* Muted lifestyle image */}
                  <div className="relative h-28 overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      className="w-full h-full object-cover filter saturate-[0.7] brightness-[0.95]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
                  </div>
                  
                  {/* Card content */}
                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <IconComponent className="w-5 h-5 text-[#8DA399]" strokeWidth={1.5} />
                      <h3 className="text-base font-semibold text-[#2C332E]">{category.name}</h3>
                    </div>
                    <p className="text-sm text-[#78716C] leading-relaxed">{category.description}</p>
                    <p className="text-xs text-[#78716C]/70 italic">{category.helper}</p>
                    
                    <button 
                      onClick={() => navigate("/store")}
                      className="text-sm text-[#8DA399] font-medium flex items-center gap-1 hover:text-[#7A8F86] transition-colors pt-2"
                    >
                      Explore Direction
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Guardrail microcopy */}
          <div className="text-center mt-10 space-y-2">
            <p className="text-sm text-[#78716C]">
              You&apos;re viewing directional examples only.
            </p>
            <p className="text-xs text-[#78716C]/70 max-w-lg mx-auto">
              Brand names, pricing, and ownership options are intentionally revealed inside the creation or ready-made experience — not here.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================== */}
      {/* SECTION 4 — TRANSITION + PATH SELECTION       */}
      {/* ============================================== */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Transition text */}
          <div className="text-center mb-10">
            <p className="text-lg text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              When you&apos;re ready to move from direction to decision, choose how you&apos;d like to continue.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* LEFT OPTION — Guided Creation */}
            <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 border-2 border-transparent hover:border-[#8DA399]/30">
              <div className="space-y-5">
                <div className="w-14 h-14 rounded-2xl bg-[#8DA399]/10 flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-[#8DA399]" />
                </div>
                <h3 className="text-2xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Begin with Guided Creation
                </h3>
                <p className="text-[#78716C] leading-relaxed">
                  Build a brand foundation at your own pace — guided, calm, and intentional.
                </p>
                <p className="text-[#8DA399] text-sm font-medium">
                  This path is for founders who want to define trust from the inside out.
                </p>
                <Button 
                  onClick={handleLogin}
                  className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-6 text-base font-medium hover-lift flex items-center gap-2 w-full justify-center"
                  data-testid="hero-start-creating-btn"
                >
                  Begin with Guided Creation
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <p className="text-sm text-[#78716C]/80 text-center">
                  No publishing. No pressure. No purchase until you&apos;re ready.
                </p>
              </div>
            </div>

            {/* RIGHT OPTION — Ready-Made Foundations */}
            <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 border-2 border-transparent hover:border-[#C77D63]/30">
              <div className="space-y-5">
                <div className="w-14 h-14 rounded-2xl bg-[#C77D63]/10 flex items-center justify-center">
                  <Package className="w-7 h-7 text-[#C77D63]" />
                </div>
                <h3 className="text-2xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Browse Ready-Made Foundations
                </h3>
                <p className="text-[#78716C] leading-relaxed">
                  Step into a brand system designed for immediate trust.
                </p>
                <p className="text-[#C77D63] text-sm font-medium">
                  Each foundation is complete, considered, and ready for ownership — including name, direction, and structure.
                </p>
                <Button 
                  onClick={() => navigate("/store")}
                  className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-6 text-base font-medium hover-lift flex items-center gap-2 w-full justify-center"
                  data-testid="hero-explore-foundations-btn"
                >
                  Browse Ready-Made Foundations
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <p className="text-sm text-[#78716C]/80 text-center">
                  Brand names and pricing appear inside this experience only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================= */}
      {/* SECTION 5 — BRAND PHILOSOPHY STATEMENT */}
      {/* ======================================= */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <p className="text-xl md:text-2xl text-[#2C332E] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
            Bloomwright Studio exists to remove the pressure from early brand decisions.
          </p>
          <div className="text-[#78716C] space-y-1">
            <p>No rushing.</p>
            <p>No second-guessing.</p>
            <p>Just clear direction — and space to grow into it.</p>
          </div>
          <p className="text-[#8DA399] font-medium pt-4">
            Trust first. Execution when you&apos;re ready.
          </p>
        </div>
      </section>

      {/* ======================= */}
      {/* SECTION 6 — FINAL CTA   */}
      {/* ======================= */}
      <section className="py-20 px-6 bg-[#8DA399]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to Begin?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Start with clarity — and build at your own pace.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleLogin}
              className="bg-white text-[#8DA399] hover:bg-[#F5F0EB] rounded-full px-8 py-6 text-lg font-medium hover-lift"
              data-testid="cta-start-creating-btn"
            >
              Begin with Guided Creation
            </Button>
            <Button 
              onClick={() => navigate("/store")}
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="cta-browse-foundations-btn"
            >
              Browse Ready-Made Foundations
            </Button>
          </div>
        </div>
      </section>

      {/* Footer with Hard Guardrail */}
      <footer className="py-12 px-6 bg-[#2C332E]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center">
              <span className="text-white font-semibold">Bloomwright Studio</span>
            </div>
            <p className="text-[#78716C]/80 text-sm text-center max-w-lg">
              Brand names, pricing, and ownership offers are intentionally separated from the introduction experience. This page sets direction. The store handles selection.
            </p>
            <p className="text-[#78716C] text-sm">
              © 2025 Bloomwright Studio Brand Builder. Empowering women entrepreneurs.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
